import React, { useMemo } from 'react';
import { ShieldAlert, AlertTriangle, Info } from 'lucide-react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps, TabletopTelemetryView } from '../../../types/second-screen.types';

const SEVERITY_ICON: Record<TabletopTelemetryView['severity'], React.ElementType> = {
  critical: ShieldAlert,
  warning: AlertTriangle,
  info: Info,
};
const SEVERITY_COLOR: Record<TabletopTelemetryView['severity'], string> = {
  critical: 'text-red-400',
  warning: 'text-amber-400',
  info: 'text-slate-400',
};

// Severity is authored by an LLM (Tabletop-Scenario-Architect) with no
// schema enforcement on the response, so a value outside the three known
// ones is possible — fall back to 'info' rather than letting an undefined
// lookup take the whole widget down (Icon rendered as `undefined` throws).
function severityIcon(severity: TabletopTelemetryView['severity']): React.ElementType {
  return SEVERITY_ICON[severity] || Info;
}
function severityColor(severity: TabletopTelemetryView['severity']): string {
  return SEVERITY_COLOR[severity] || SEVERITY_COLOR.info;
}

function formatSimOffset(atSimMs: number): string {
  const totalMinutes = Math.max(0, Math.floor(atSimMs / 60_000));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return `T+${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

const SocConsoleWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';
  const telemetry = state?.tabletop?.telemetry ?? [];
  const totalSystemsCount = state?.tabletop?.totalSystemsCount ?? 0;

  // Affected-system status board: latest severity per named system, over
  // telemetry the backend has already filtered to what's due at sim time.
  const systemStatus = useMemo(() => {
    const bySystem = new Map<string, TabletopTelemetryView>();
    for (const entry of telemetry) {
      if (!entry.systemAffected) continue;
      bySystem.set(entry.systemAffected, entry); // telemetry is chronological, last write wins
    }
    return Array.from(bySystem.values());
  }, [telemetry]);

  const degradedCount = systemStatus.filter((s) => s.severity === 'warning' || s.severity === 'critical').length;

  const recentAlerts = telemetry.slice(-8).reverse();

  if (telemetry.length === 0) {
    return (
      <div className={`flex items-center justify-center h-full text-sm ${isDark ? 'text-white/30' : 'text-slate-400'}`}>
        No telemetry yet
      </div>
    );
  }

  return (
    <div className="flex h-full px-3 py-2 gap-3">
      {/* Affected-system status board */}
      {systemStatus.length > 0 && (
        <div className="flex flex-col flex-shrink-0 w-36 min-h-0">
          <p className={`flex-shrink-0 text-sm font-semibold mb-1 ${
            degradedCount > 0 ? 'text-amber-400' : isDark ? 'text-white/50' : 'text-slate-500'
          }`}>
            {degradedCount} of {totalSystemsCount} systems degraded
          </p>
          <div className="flex flex-col gap-1 overflow-y-auto min-h-0">
            {systemStatus.map(s => (
              <div key={s.systemAffected} className={`flex items-center justify-between text-sm px-1.5 py-1 rounded flex-shrink-0 ${
                s.severity === 'critical' ? 'bg-red-500/15' : s.severity === 'warning' ? 'bg-amber-500/10' : 'bg-white/5'
              }`}>
                <span className={isDark ? 'text-white/70 truncate' : 'text-slate-600 truncate'}>{s.systemAffected}</span>
                <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ml-1 ${
                  s.severity === 'critical' ? 'bg-red-400' : s.severity === 'warning' ? 'bg-amber-400' : 'bg-slate-400'
                }`} />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Synthetic alert stream */}
      <div className="flex-1 flex flex-col gap-1.5 overflow-y-auto min-w-0">
        {recentAlerts.map(alert => {
          const Icon = severityIcon(alert.severity);
          return (
            <div key={alert.seq} className="flex items-start gap-1.5 flex-shrink-0">
              <Icon size={12} className={`mt-0.5 flex-shrink-0 ${severityColor(alert.severity)}`} />
              <p className={`text-sm leading-tight ${isDark ? 'text-white/80' : 'text-slate-700'}`}>
                <span className={`mr-1.5 font-mono ${isDark ? 'text-white/40' : 'text-slate-400'}`}>{formatSimOffset(alert.atSimMs)}</span>
                {alert.label}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SocConsoleWidget;
